"use client";
// @ts-strict-ignore
import styled from "styled-components";

type ResponsiveGridProps = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type FallbackProps = "inherit" | "initial" | "unset";
type AlignmentProps =
  | FallbackProps
  | "center"
  | "start"
  | "end"
  | "flex-start"
  | "flex-end"
  | "normal"
  | "stretch"
  | "safe center"
  | "unsafe center";
type JustifyContentProps =
  | AlignmentProps
  | "left"
  | "right"
  | "space-between"
  | "space-around"
  | "space-evenly";
type AlignItemProps =
  | AlignmentProps
  | "baseline"
  | "first baseline"
  | "last baseline";
type FlexDirectionProps =
  | FallbackProps
  | "column"
  | "column-reverse"
  | "row"
  | "row-reverse"
  | "revert";

interface RowProps {
  justifyContent?: JustifyContentProps;
  alignItems?: AlignItemProps;
  flexDirection?: FlexDirectionProps;
  flex?: string;
}

export interface ColProps {
  sm?: ResponsiveGridProps;
  md?: ResponsiveGridProps;
  lg?: ResponsiveGridProps;
  xl?: ResponsiveGridProps;
  justifyContent?: JustifyContentProps;
  alignItems?: AlignItemProps;
  flexDirection?: FlexDirectionProps;
  flex?: string;
  width?: string;
  maxWidth?: string;
  gap?: string;
}

export interface StackProps {
  smSpacing?: number;
  mdSpacing?: number;
  lgSpacing?: number;
  smPt?: number;
  mdPt?: number;
  lgPt?: number;
}

function generateColumnWidthStyles(size) {
  if (size === 0) return `display: none`;
  if (!size) return null;
  const widthPercent = Math.round((size / 12) * 10e7) / 10e5;
  return `
    display: flex;
    flex-basis: ${widthPercent}%;
    flex-grow: 0;
    max-width: ${widthPercent}%;
  `;
}

export const Vstack = styled.div<StackProps>`
  width: 100%;
  & > * {
    margin-top: ${({ smSpacing = 48 }) => smSpacing / 2}px;
    margin-bottom: ${({ smSpacing = 48 }) => smSpacing / 2}px;
  }
  ${(props) =>
    props.mdSpacing &&
    props.theme.media.tablet`
      & > * {
        margin-top: ${({ mdSpacing }) => mdSpacing / 2}px;
        margin-bottom: ${({ mdSpacing }) => mdSpacing / 2}px;
      }
  `}
  ${(props) =>
    props.lgSpacing &&
    props.theme.media.desktop`
      & > * {
        margin-top: ${({ lgSpacing }) => lgSpacing / 2}px;
        margin-bottom: ${({ lgSpacing }) => lgSpacing / 2}px;
      }
  `}

  ${(props) =>
    props.smPt &&
    props.theme.media.mobile`
        & > * {
          padding-top: ${({ smPt }) => smPt}px;
        }
    `}

  ${(props) =>
    props.mdPt &&
    props.theme.media.tablet`
      & > * {
        padding-top: ${({ mdPt }) => mdPt}px;
      }
  `}
  ${(props) =>
    props.lgPt &&
    props.theme.media.desktop`
      & > * {
        padding-top: ${({ lgPt }) => lgPt}px;
      }
  `}
`;

export const Grid = styled(Vstack)<StackProps>`
  max-width: 1200px;
  margin: auto;
`;

/**
 * @deprecated
 * Use NewRow below instead
 */
export const Row = styled.div<RowProps>`
  display: flex;
  flex-wrap: wrap;
  ${({ noMargin }) =>
    noMargin &&
    `
    margin-top: 0px;
    margin-bottom: 0px;
  `}

  ${({ flexDirection }) =>
    flexDirection &&
    `
    flex-direction: ${flexDirection};
  `}
  ${({ justifyContent }) =>
    justifyContent &&
    `
    justify-content: ${justifyContent};
  `}
  ${({ alignItems }) =>
    alignItems &&
    `
    align-items: ${alignItems};
  `}
  margin-right: -24px;
  margin-left: -24px;
  ${(props) =>
    props.theme.media.tablet`
      margin-left: -32px;
      margin-right: -32px;
  `}
  & > * {
    padding-left: 24px;
    padding-right: 24px;
    ${(props) =>
      props.theme.media.tablet`
        padding-left: 32px;
        padding-right: 32px;
    `}
  }
`;

// Duplicating the Row component without a x-axis margins to fix css issues caused by margin side effects in the /about page. It wouldn't be a good idea to change the original Row component because it is used in 154 files 🤯
export const NewRow = styled.div<RowProps>`
  display: flex;
  flex-wrap: wrap;
  ${({ noMargin }) =>
    noMargin &&
    `
    margin-top: 0px;
    margin-bottom: 0px;
  `}

  ${({ flexDirection }) =>
    flexDirection &&
    `
    flex-direction: ${flexDirection};
  `}
  ${({ justifyContent }) =>
    justifyContent &&
    `
    justify-content: ${justifyContent};
  `}
  ${({ alignItems }) =>
    alignItems &&
    `
    align-items: ${alignItems};
  `}
`;

export const Col = styled.div<ColProps>`
  display: flex;
  ${({ gap }) =>
    gap &&
    `
    gap: ${gap};
  `}
  ${({ justifyContent }) =>
    justifyContent &&
    `
    justify-content: ${justifyContent};
  `}
  ${({ alignItems }) =>
    alignItems &&
    `
    align-items: ${alignItems};
  `}
  ${({ flexDirection }) =>
    flexDirection &&
    `
    flex-direction: ${flexDirection};
  `}
  box-sizing: border-box;
  ${({ flex }) => flex && `flex: ${flex}`};
  ${({ width }) => width && `width: ${width}`};
  ${({ maxWidth }) => maxWidth && `max-width: ${maxWidth}`};

  ${({ sm }) => generateColumnWidthStyles(sm)};
  ${(props) => props.theme.media.tablet`
    ${generateColumnWidthStyles(props.md)}
  `}
  ${(props) => props.theme.media.desktop`
    ${generateColumnWidthStyles(props.lg)}
  `}
  ${(props) => props.theme.media.largeDesktop`
    ${generateColumnWidthStyles(props.xl)}
  `}

  & > * {
    ${({ textColor, theme }) =>
      textColor &&
      `
    color: ${theme.textColors[textColor] || textColor};
  `}
  }
`;
